<template>
  <v-row no-gutters>
    <v-col class="pt-0 pb-10">
      <sidebar-row :has-icon-column="false">
        <v-tabs v-model="currentTab" hide-slider class="custom-tabs pt-6">
          <v-tooltip bottom color="font" v-for="tab in computedTabs" :key="tab.value" :disabled="tab.isTooltipDisabled">
            <template #activator="{ on }">
              <v-row no-gutters class="pt-2">
                <v-col cols="12" v-on="on" class="extra-div d-flex">
                  <v-badge
                    overlap
                    :content="errorsCount[tab.value]"
                    :value="errorsCount && errorsCount[tab.value] ? errorsCount[tab.value] : 0"
                    color="error"
                    :offset-y="-18"
                  >
                    <v-tab v-text="tab.text" :disabled="tab.isTabDisabled" active-class="activeTab"> </v-tab>
                  </v-badge>
                </v-col>
              </v-row>
            </template>
            {{ tab.tooltipText }}
          </v-tooltip>
        </v-tabs>
      </sidebar-row>
      <v-row no-gutters class="pr-5 pt-1">
        <v-col class="pa-0">
          <v-tabs-items v-model="currentTab">
            <v-tab-item v-for="tab in computedTabs" :key="tab.value">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-row class="pt-1 flex-nowrap" no-gutters align="center">
                    <template v-if="isAssignedFeaturesLoading">
                      <v-col>
                        <main-loader />
                      </v-col>
                    </template>
                    <template v-else-if="assignedFeatures[tab.value]">
                      <v-col cols="1"></v-col>
                      <v-col cols="9">
                        <v-row>
                          <v-col cols="auto" class="pr-2 text-body-3 font-weight-medium">
                            {{ $i18n.t('sidebar.zdmEditor.copyFrom') }}:
                          </v-col>
                          <v-col
                            cols="auto"
                            class="text-body-3 pr-2 font-weight-medium"
                            :style="`color: ${$_colors.primary}`"
                          >
                            {{
                              assignedFeatures[tab.value][
                                zdmDataDataSourcesDisplayAttributesNames[tab.value].split('.')[1]
                              ]
                            }}
                          </v-col>
                        </v-row>
                        <v-row
                          v-if="currentTabName === 'street_lamp' && assignedFeatures[tab.value].treeObjects"
                          no-gutters
                          style="margin-left: -17px"
                        >
                          <v-col class="pr-2">
                            <v-chip-group class="lantern-chips-group" style="z-index: 1">
                              <v-tooltip
                                bottom
                                color="font"
                                v-for="(object, index) in assignedFeatures[tab.value].treeObjects"
                                :key="index"
                              >
                                <template #activator="{ on }">
                                  <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                    {{ object.value }}
                                  </v-chip>
                                </template>
                                {{ $i18n.t(`sidebar.zdmEditor.${object.name}_model`) }}
                              </v-tooltip>
                            </v-chip-group>
                          </v-col>
                        </v-row>
                        <v-row v-if="tab.value === 'cable'" no-gutters style="margin-left: -17px">
                          <v-col class="pr-2">
                            <v-chip-group class="lantern-chips-group" style="z-index: 1">
                              <v-tooltip bottom color="font" v-if="assignedFeatures[tab.value].catalogCableType">
                                <template #activator="{ on }">
                                  <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                    {{ assignedFeatures[tab.value].catalogCableType }}
                                  </v-chip>
                                </template>
                                {{ $i18n.t('sidebar.zdmEditor.cableType') }}
                              </v-tooltip>
                              <v-tooltip bottom color="font" v-if="assignedFeatures[tab.value].metadata.start_p">
                                <template #activator="{ on }">
                                  <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                    {{
                                      `${$i18n.t('default.from')}: ${assignedFeatures[tab.value].metadata.start_p.name}`
                                    }}
                                  </v-chip>
                                </template>
                                {{ $i18n.t('sidebar.zdmEditor.fromDevice') }}
                              </v-tooltip>
                              <v-tooltip bottom color="font" v-if="assignedFeatures[tab.value].metadata.end_p">
                                <template #activator="{ on }">
                                  <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                    {{ `${$i18n.t('default.to')}: ${assignedFeatures[tab.value].metadata.end_p.name}` }}
                                  </v-chip>
                                </template>
                                {{ $i18n.t('sidebar.zdmEditor.toDevice') }}
                              </v-tooltip>
                            </v-chip-group>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="auto" class="ml-auto">
                        <v-tooltip bottom color="font">
                          <template #activator="{ on }">
                            <v-icon v-on="on" @click="zoomToAssignFeature" color="rgba(0,0,0,.7)" size="19px">
                              mdi-magnify
                            </v-icon>
                          </template>
                          {{ $i18n.t('navbar.zoomIn') }}
                        </v-tooltip>
                      </v-col>
                      <v-col cols="auto">
                        <dots-menu
                          :items="dotsItems"
                          @delete="unassignFeature"
                          @details="copyPreviewObjectFromSession"
                        />
                      </v-col>
                    </template>
                    <template v-else>
                      <v-col cols="1"></v-col>
                      <v-col cols="auto" class="pr-4 text-body-3 font-weight-medium">
                        {{ $i18n.t('sidebar.zdmEditor.copyFrom') }}:
                      </v-col>
                      <v-col>
                        <data-input
                          :ref="`features-input-tab-${tab.value}`"
                          simple
                          :data-type="{ name: 'features' }"
                          @pushFeatures="assignFeature"
                          :featuresSettings="copyFeaturesSettings"
                          translationPath="sidebar.zdmEditor.selectFromMap"
                          :disabled="isLoading"
                        />
                      </v-col>
                      <v-col class="font-italic font-weight-light italic--text">
                        {{ $_loweralize($i18n.t('default.or')) }}
                      </v-col>
                      <v-col>
                        <data-input
                          dense
                          hide-details
                          :label="$i18n.t('sidebar.zdmEditor.number')"
                          :data-type="{ name: 'text' }"
                          v-model="assignedFeaturesInput[tab.value]"
                          @keydown.enter="getFeatureToAssign"
                          :disabled="isLoading"
                        />
                      </v-col>
                    </template>
                  </v-row>
                  <v-row no-gutters class="pt-5">
                    <v-col cols="1"></v-col>
                    <v-col cols="auto" class="pr-1">
                      <data-input
                        :ref="`feature-to-add-input-${tab.value}`"
                        :dataType="{ name: 'geometry' }"
                        name="zdmEditorFeatureToAdd"
                        button-component="baseRoundedButton"
                        :buttonProps="{
                          icon: 'mdi-plus',
                          size: 'x-small',
                          disableDefaultMargin: true,
                        }"
                        :geometry-type="currentTabName === 'cable' ? 'LineString' : 'Point'"
                        snapping
                        :snapping-additional-layers="snappingAdditionalLayers"
                        :snappingActiveLayerId="currentTabName === 'cable' ? currentTabLayer : null"
                        :tooltip="$i18n.t('sidebar.zdmEditor.addFeature')"
                        v-model="featureToAddGeometry"
                        :disabled="isLoading || isAssignedFeaturesLoading"
                        disable-editing
                      >
                      </data-input>
                    </v-col>
                    <v-col cols="auto">
                      <data-input
                        simple
                        rounded
                        ref="featureToDeleteButton"
                        v-bind="featureToDeleteInput"
                        :featuresSettings="deleteFeaturesSettings"
                        :tooltip="$i18n.t('sidebar.zdmEditor.deleteFeature')"
                        @pushFeatures="handleAddFeatureToDelete"
                      />
                    </v-col>
                  </v-row>
                  <!-- Dodane -->
                  <template v-if="currentTabSessionObjectsInsert.length > 0">
                    <v-row no-gutters class="pt-5">
                      <v-col cols="1"></v-col>
                      <v-col cols="11">
                        <card-title-row
                          is-subtitle
                          :has-icon-column="false"
                          translation-path="sidebar.zdmEditor.objectsAdded"
                          class="pt-2"
                        />
                      </v-col>
                    </v-row>
                    <v-row no-gutters class="justify-end">
                      <v-col cols="1"></v-col>
                      <v-col cols="auto">
                        <clickable-text @click="handleDeleteObjectsFromSession('insert')">
                          {{ $i18n.t('sidebar.zdmEditor.clearAll') }}
                        </clickable-text>
                      </v-col>
                    </v-row>
                    <v-row no-gutters class="pb-1">
                      <v-col cols="12">
                        <v-list class="py-0">
                          <template
                            v-for="(object, idx) in tabsListExpanded[tab.value] && tabsListExpanded[tab.value]['insert']
                              ? currentTabSessionObjectsInsert
                              : currentTabSessionObjectsInsert.slice(0, MAX_ELEMENTS_ON_LISTS)"
                          >
                            <v-row no-gutters :key="idx" align="center">
                              <v-col cols="1">
                                <base-rounded-button
                                  v-if="object.error"
                                  :tooltip="object.error"
                                  icon="mdi-exclamation"
                                  icon-size="16"
                                  :custom-size="20"
                                  color="error"
                                />
                              </v-col>
                              <v-col cols="11">
                                <v-list-item
                                  class="list-element-insert px-0 py-0"
                                  :class="{
                                    'py-1':
                                      (currentTabName === 'street_lamp' && object.metadata.street_lamp_objects) ||
                                      (currentTabName === 'cable' &&
                                        (object.metadata.start_p || object.metadata.end_p)),
                                  }"
                                  :key="idx"
                                >
                                  <v-list-item-content class="pa-0">
                                    <v-list-item-title>
                                      <v-row no-gutters style="align-items: center; padding: 0 17px">
                                        <v-col cols="auto">
                                          <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                                            {{
                                              `${$i18n.t(`sidebar.zdmSingleObject.${currentTabName}`)}: ${
                                                object.properties[currentTabDataSourceDisplayAttributeName]
                                              }`
                                            }}
                                          </v-subheader>
                                        </v-col>
                                      </v-row>
                                      <v-row
                                        no-gutters
                                        v-if="currentTabName === 'street_lamp' && object.metadata.street_lamp_objects"
                                      >
                                        <v-col cols="auto">
                                          <v-chip-group class="lantern-chips-group" style="z-index: 1">
                                            <v-tooltip
                                              bottom
                                              color="font"
                                              v-for="(object, index) in getChipsFromTree(
                                                object.properties,
                                                object.metadata.street_lamp_objects
                                              )"
                                              :key="index"
                                            >
                                              <template #activator="{ on }">
                                                <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                                  {{ object.value }}
                                                </v-chip>
                                              </template>
                                              {{ $i18n.t(`sidebar.zdmEditor.${object.name}_model`) }}
                                            </v-tooltip>
                                          </v-chip-group>
                                        </v-col>
                                      </v-row>
                                      <v-row no-gutters v-if="currentTabName === 'cable'">
                                        <v-col cols="auto">
                                          <v-chip-group class="lantern-chips-group" style="z-index: 1">
                                            <v-tooltip bottom color="font" v-if="getCatalogCableType(object)">
                                              <template #activator="{ on }">
                                                <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                                  {{ getCatalogCableType(object) }}
                                                </v-chip>
                                              </template>
                                              {{ $i18n.t('sidebar.zdmEditor.cableType') }}
                                            </v-tooltip>
                                            <template v-if="object.metadata.start_p || object.metadata.end_p">
                                              <v-tooltip bottom color="font" v-if="object.metadata.start_p">
                                                <template #activator="{ on }">
                                                  <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                                    {{ `${$i18n.t('default.from')}: ${object.metadata.start_p.name}` }}
                                                  </v-chip>
                                                </template>
                                                {{ $i18n.t('sidebar.zdmEditor.fromDevice') }}
                                              </v-tooltip>
                                              <v-tooltip bottom color="font" v-if="object.metadata.end_p">
                                                <template #activator="{ on }">
                                                  <v-chip v-on="on" :ripple="false" class="caption" x-small>
                                                    {{ `${$i18n.t('default.to')}: ${object.metadata.end_p.name}` }}
                                                  </v-chip>
                                                </template>
                                                {{ $i18n.t('sidebar.zdmEditor.toDevice') }}
                                              </v-tooltip>
                                            </template>
                                          </v-chip-group>
                                        </v-col>
                                      </v-row>
                                    </v-list-item-title>
                                  </v-list-item-content>
                                  <v-list-item-action>
                                    <v-row no-gutters align="center">
                                      <v-col cols="auto">
                                        <v-tooltip bottom color="font">
                                          <template #activator="{ on }">
                                            <v-icon
                                              v-on="on"
                                              @click="zoomToFeature(object)"
                                              color="rgba(0,0,0,.7)"
                                              size="19px"
                                              :disabled="isObjectDeletingFromSession || isLoading"
                                            >
                                              mdi-magnify
                                            </v-icon>
                                          </template>
                                          {{ $i18n.t('navbar.zoomIn') }}
                                        </v-tooltip>
                                      </v-col>
                                      <v-col cols="auto">
                                        <dots-menu
                                          :items="addedObjectsDotsItems"
                                          @delete="handleDeleteObjectFromSession(object)"
                                          @edit="editObjectFromSession(object)"
                                          :disabled="isObjectDeletingFromSession || isLoading"
                                        />
                                      </v-col>
                                    </v-row>
                                  </v-list-item-action>
                                </v-list-item>
                              </v-col>
                            </v-row>
                          </template>
                        </v-list>
                        <v-list-item
                          v-if="currentTabSessionObjectsInsert.length > MAX_ELEMENTS_ON_LISTS"
                          class="px-0"
                          style="max-height: 30px; min-height: 0px"
                        >
                          <v-col cols="1" />
                          <v-list-item-action class="ma-0">
                            <link-underlined
                              @click="toggleTabList('insert')"
                              :translation-path="
                                tabsListExpanded[tab.value] && tabsListExpanded[tab.value]['insert']
                                  ? 'sidebar.showLess'
                                  : 'sidebar.showMore'
                              "
                            />
                          </v-list-item-action>
                        </v-list-item>
                      </v-col>
                    </v-row>
                  </template>
                  <!-- Usunięte -->
                  <template v-if="currentTabSessionObjectsDelete.length > 0">
                    <v-row no-gutters class="pt-5">
                      <v-col cols="1"></v-col>
                      <v-col cols="11">
                        <card-title-row
                          is-subtitle
                          :has-icon-column="false"
                          translation-path="sidebar.zdmEditor.objectsDeleted"
                          class="pt-2"
                        />
                      </v-col>
                    </v-row>
                    <v-row no-gutters class="justify-end">
                      <v-col cols="1"></v-col>
                      <v-col cols="auto">
                        <clickable-text @click="handleDeleteObjectsFromSession('delete')">
                          {{ $i18n.t('sidebar.zdmEditor.clearAll') }}
                        </clickable-text>
                      </v-col>
                    </v-row>
                    <v-row no-gutters class="pb-1">
                      <v-col cols="12">
                        <v-list class="py-0">
                          <template
                            v-for="(object, idx) in tabsListExpanded[tab.value] && tabsListExpanded[tab.value]['delete']
                              ? currentTabSessionObjectsDelete
                              : currentTabSessionObjectsDelete.slice(0, MAX_ELEMENTS_ON_LISTS)"
                          >
                            <v-row no-gutters :key="idx" align="center">
                              <v-col cols="1">
                                <base-rounded-button
                                  v-if="object.error"
                                  :tooltip="object.error"
                                  icon="mdi-exclamation"
                                  icon-size="16"
                                  :custom-size="20"
                                  color="error"
                                />
                              </v-col>
                              <v-col cols="11">
                                <v-list-item class="list-element-delete px-2">
                                  <v-list-item-content class="pa-0">
                                    <v-list-item-title style="padding: 0 17px">
                                      <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                                        {{
                                          `${$i18n.t(`sidebar.zdmSingleObject.${currentTabName}`)}: ${
                                            object.properties[currentTabDataSourceDisplayAttributeName]
                                          }`
                                        }}
                                      </v-subheader>
                                    </v-list-item-title>
                                  </v-list-item-content>
                                  <v-list-item-action>
                                    <v-row no-gutters align="center">
                                      <v-col cols="auto">
                                        <v-tooltip bottom color="font">
                                          <template #activator="{ on }">
                                            <v-icon
                                              v-on="on"
                                              @click="zoomToFeature(object)"
                                              color="rgba(0,0,0,.7)"
                                              size="19px"
                                              :disabled="isObjectDeletingFromSession || isLoading"
                                            >
                                              mdi-magnify
                                            </v-icon>
                                          </template>
                                          {{ $i18n.t('navbar.zoomIn') }}
                                        </v-tooltip>
                                      </v-col>
                                      <v-col cols="auto">
                                        <dots-menu
                                          :items="deletedObjectsDotsItems"
                                          @delete="handleDeleteObjectFromSession(object)"
                                          :disabled="isObjectDeletingFromSession || isLoading"
                                        />
                                      </v-col>
                                    </v-row>
                                  </v-list-item-action>
                                </v-list-item>
                              </v-col>
                            </v-row>
                          </template>
                        </v-list>
                        <v-list-item
                          v-if="currentTabSessionObjectsDelete.length > MAX_ELEMENTS_ON_LISTS"
                          class="px-0"
                          style="max-height: 30px; min-height: 0px"
                        >
                          <v-col cols="1" />
                          <v-list-item-action class="ma-0">
                            <link-underlined
                              @click="toggleTabList('delete')"
                              :translation-path="
                                tabsListExpanded[tab.value] && tabsListExpanded[tab.value]['delete']
                                  ? 'sidebar.showLess'
                                  : 'sidebar.showMore'
                              "
                            />
                          </v-list-item-action>
                        </v-list-item>
                      </v-col>
                    </v-row>
                  </template>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-col>
    <zdm-editor-feature-dialog
      v-if="isFeatureDialogLoaded"
      :is-visible.sync="isFeatureDialogVisible"
      v-bind="featureDialogBind"
    />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import { debounce } from 'lodash';
import { never, primaryAction } from 'ol/events/condition';
import { prepareDrawnFeature } from '@/assets/js/mapUtils';

import CardTitleRow from '@/components/CardTitleRow';
import ClickableText from '@/components/ClickableText';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import LinkUnderlined from '@/components/LinkUnderlined';
import SidebarRow from '@/components/SidebarRow';
import MainLoader from '@/components/MainLoader';

import styles from '@/mixins/map/styles';
import zdmEditorSettings from '@/mixins/zdmEditorSettings';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'ZdmEditorFeaturesTabs',
  components: {
    CardTitleRow,
    ClickableText,
    DataInput,
    DotsMenu,
    LinkUnderlined,
    SidebarRow,
    MainLoader,
    ZdmEditorFeatureDialog: () => import('@/components/ZdmEditorFeatureDialog'),
  },
  mixins: [styles, zdmEditorSettings, prepareAttributesFeatureCardMixin],
  props: {
    errorsCount: {
      type: Object,
    },
    tabs: {
      type: Array,
      default: () => {
        return [
          {
            value: 'street_lamp',
          },
          {
            value: 'cable',
          },
          {
            value: 'cabinet',
          },
          {
            value: 'power_station',
          },
        ];
      },
    },
    validation: {
      type: Object,
    },
  },
  computed: {
    isModificationInteractionActive: get('edit/isModificationInteractionActive'),
    isDrawingInteractionActive: get('edit/isDrawingInteractionActive'),
    activeTool: get('tools/activeTool'),
    layersMetadata: get('layers/metadata'),
    layers: get('layers/layers'),

    cableDisplayName: get('admin/modulesMapping@zdm_data.catalogs.cable.display_attribute_name'),
    pickArmDisplayName: get('admin/modulesMapping@zdm_data.catalogs.pick_arm.display_attribute_name'),
    headDisplayName: get('admin/modulesMapping@zdm_data.catalogs.head.display_attribute_name'),
    poleDisplayName: get('admin/modulesMapping@zdm_data.catalogs.pole.display_attribute_name'),
    dataSourcesFeatures: get('layers/dataSourcesFeatures'),
    currentSessionId: get('zdm_editor/currentSessionId'),
    session: get('zdm_editor/session'),
    zdmDataDataSources: get('admin/modulesMapping@zdm_data.datasources'),
    zdmDataLayers: get('admin/modulesMapping@zdm_data.layers'),
    projectElements: get('layers/project@layers'),
    poleTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pole'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    pickArmFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_pick_arm'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    headTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_head'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    cableTypeFeatures() {
      return this.dataSourcesFeatures['zdm_data_catalog_cable'].features.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    computedTabs() {
      return this.tabs.map(tab => {
        return {
          ...tab,
          text: this.$i18n.t(`admin.zdmStreetLights.catalogs.${tab.value}`),
          tooltipText: this.tooltipText(tab.value),
          isTabDisabled: this.isDisabled(tab.value),
          isTooltipDisabled: !this.isDisabled(tab.value),
        };
      });
    },
    currentTabName() {
      return this.computedTabs[this.currentTab]?.value || '';
    },
    currentTabDataSource() {
      return this.zdmDataDataSources[this.currentTabName]?.datasource;
    },
    currentTabLayer() {
      return this.zdmDataLayers[this.currentTabName];
    },
    currentTabDataSourceDisplayAttributeName() {
      return this.zdmDataDataSourcesDisplayAttributesNames[this.currentTabName];
    },
    currentTabSessionObjects() {
      return this.session.session_objects?.[this.currentTabDataSource] || {};
    },
    currentTabSessionObjectsInsert() {
      if (!this.validation) {
        return this.currentTabSessionObjects['insert'] || [];
      }
      return (this.currentTabSessionObjects['insert'] || []).map(feature => {
        const featureIdSession = feature.metadata.id;
        let error = '';
        if ((this.validation.duplicates || []).includes(featureIdSession)) {
          error += `${error ? '; ' : ''}${this.$i18n.t('sidebar.zdmEditor.errors.duplicate', {
            type: this.$_loweralize(this.$i18n.t(`sidebar.zdmSingleObject.${this.currentTabName}`)),
          })}`;
        }
        if (this.validation.modified?.[featureIdSession]) {
          error += `${error ? '; ' : ''}${this.$i18n.t(
            'sidebar.zdmEditor.errors.attributesModified'
          )}: ${this.validation.modified[featureIdSession].join(',')}`;
        }
        if ((this.validation.cables || []).includes(featureIdSession)) {
          error += `${error ? '; ' : ''}${this.$i18n.t('sidebar.zdmEditor.errors.cable')}`;
        }
        if ((this.validation.numbers || []).includes(featureIdSession)) {
          error += `${error ? '; ' : ''}${this.$i18n.t('sidebar.zdmEditor.errors.numbers')}`;
        }
        return {
          ...feature,
          error,
        };
      });
    },
    currentTabSessionObjectsDelete() {
      if (!this.validation) {
        return this.currentTabSessionObjects['delete'] || [];
      }
      return (this.currentTabSessionObjects['delete'] || []).map(feature => {
        const featureIdSession = feature.metadata.id;
        let error = '';
        if ((this.validation.deleted || []).includes(featureIdSession)) {
          error += `${error ? '; ' : ''}${this.$i18n.t('sidebar.zdmEditor.errors.delete')}`;
        }
        if (this.validation.modified?.[featureIdSession]) {
          error += `${error ? '; ' : ''}${this.$i18n.t(
            'sidebar.zdmEditor.errors.attributesModified'
          )} ${this.validation.modified[featureIdSession].join(',')}`;
        }
        return {
          ...feature,
          error,
        };
      });
    },
    featureDialogBind() {
      return {
        copy: this.assignedFeatures[this.currentTabName] || undefined,
        dataSource: this.currentTabDataSource,
        edit: this.currentEditFeature,
        layer: this.currentTabLayer,
        geometry: this.featureToAddGeometry,
        readOnly: this.isCopyPreview,
        sessionId: this.currentSessionId,
        tabName: this.currentTabName,
        tabSessionObjects: this.currentTabSessionObjects,
      };
    },
    featureToDeleteInput() {
      return {
        dataType: {
          name: 'features',
        },
        buttonProps: {
          icon: 'mdi-delete',
          size: 'x-small',
          color: 'rgba(225,225,225,1)',
          iconColor: 'rgba(0,0,0,0.7)',
        },
        disabled: this.isLoading,
      };
    },
    snappingAdditionalLayers() {
      return [
        { group: 'zdmEditorLayers', id: 'street_lamp', gjson: true },
        { group: 'zdmEditorLayers', id: 'cabinet', gjson: true },
        { group: 'zdmEditorLayers', id: 'power_station', gjson: true },
        ...(this.currentTabName === 'cable' ? [] : [{ group: 'zdmEditorLayers', id: 'cable', gjson: true }]),
      ];
    },
    zdmDataDataSourcesDisplayAttributesNames() {
      const zdmDataDataSourcesDisplayAttributesNames = {};
      this.tabs.forEach(tab => {
        const { value } = tab;
        zdmDataDataSourcesDisplayAttributesNames[value] = this.zdmDataDataSources[value].display_attribute_name;
      });
      return zdmDataDataSourcesDisplayAttributesNames;
    },
    dotsItems() {
      return [{ name: 'details' }, { name: 'delete' }];
    },
    copyFeaturesSettings() {
      return {
        layers: {
          included: [this.currentTabLayer, this.currentTabName],
        },
      };
    },
    deleteFeaturesSettings() {
      return {
        layers: {
          included: [this.currentTabLayer],
        },
      };
    },
  },
  data: vm => ({
    isAssignedFeaturesLoading: false,
    MAX_ELEMENTS_ON_LISTS: 5,
    addedObjectsDotsItems: [
      {
        name: 'edit',
      },
      {
        name: 'delete',
        customName: vm.$i18n.t('menu.deleteFromList'),
      },
    ],
    deletedObjectsDotsItems: [
      {
        name: 'delete',
        customName: vm.$i18n.t('menu.deleteFromList'),
      },
    ],
    assignedFeatures: {},
    assignedFeaturesInput: {},
    currentEditFeature: undefined,
    currentTab: null,
    isFeatureDialogLoaded: false,
    isFeatureDialogVisible: false,
    featureToAddGeometry: null,
    tabsListExpanded: {},

    isObjectDeletingFromSession: false,
    isLoading: false,
    isCopyPreview: false,
  }),
  methods: {
    getZdmObjectTreeStructure: call('zdm/getZdmObjectTreeStructure'),
    addFeatureToDelete: call('zdm_editor/addFeatureToDelete'),
    deleteObjectFromSession: call('zdm_editor/deleteObjectFromSession'),
    deleteObjectsFromSession: call('zdm_editor/deleteObjectsFromSession'),
    editFeatureFromSession: call('zdm_editor/editFeatureFromSession'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    getLayerFeature: call('layers/getLayerFeature'),
    getCableRelations: call('zdm_editor/getCableRelations'),
    getCatalogCableType(feature) {
      const cableType = this.cableTypeFeatures.find(
        cableTypeFeature =>
          cableTypeFeature.id === feature.properties?.[this.zdmDataDataSources.cable.catalog_attribute_name]
      );
      return cableType?.[this.cableDisplayName] || '';
    },
    getChipsFromTree(featureProperties, treeMetadata) {
      let polesArray = [];
      let pickArmArray = [];
      let headObj = {};
      treeMetadata.pole.forEach(pole => {
        polesArray.push({
          name: 'pole',
          value: this.poleTypeFeatures.find(
            ft => ft.id === pole.properties[this.zdmDataDataSources.pole.catalog_attribute_name]
          )[this.poleDisplayName],
        });
        pole.pick_arm.map(pickArm => {
          if (pickArm.main) {
            pickArmArray.push({
              name: 'pick_arm',
              value: this.pickArmFeatures.find(
                ft => ft.id === pickArm.properties[this.zdmDataDataSources.pick_arm.catalog_attribute_name]
              )[this.pickArmDisplayName],
            });
          }
          pickArm.head.map(headE => {
            if (headObj[headE.properties[this.zdmDataDataSources.head.catalog_attribute_name]]) return;
            headObj[headE.properties[this.zdmDataDataSources.head.catalog_attribute_name]] = {
              name: 'head',
              value: this.headTypeFeatures.find(
                ft => ft.id === headE.properties[this.zdmDataDataSources.head.catalog_attribute_name]
              )[this.headDisplayName],
            };
          });
        });
      });
      const featureStreet = featureProperties[this.zdmDataDataSources[this.currentTabName].street_attribute_name];
      const street = featureStreet
        ? [
            {
              name: 'street',
              value: featureStreet,
            },
          ]
        : [];
      return [...street, ...polesArray, ...pickArmArray, ...Object.values(headObj)];
    },
    async handleDeleteObjectFromSession(object) {
      const payload = {
        sessionId: this.currentSessionId,
        featureId: object.metadata.id,
        dataSourcesToRefresh: [this.currentTabDataSource],
      };
      this.isObjectDeletingFromSession = true;
      try {
        await this.deleteObjectFromSession(payload);
        this.$root.$emit('deleteModifySidebarFeature', object.metadata.id);
        const { layerId, id } = this.assignedFeatures[this.currentTabName];
        if (layerId === this.currentTabName && id === object.metadata.id) {
          this.unassignFeature();
        }
        this.isObjectDeletingFromSession = false;
      } catch {
        this.isObjectDeletingFromSession = false;
      }
    },
    async handleDeleteObjectsFromSession(operation) {
      this.isObjectDeletingFromSession = true;
      try {
        const payload = {
          sessionId: this.currentSessionId,
          operation,
          dataSource: this.currentTabDataSource,
        };
        await this.deleteObjectsFromSession(payload);
        this.startCurrentTabFeaturesModify();
        this.isObjectDeletingFromSession = false;
      } catch {
        this.isObjectDeletingFromSession = false;
      }
    },
    async handleAddFeatureToDelete(feature) {
      if (feature.length) {
        this.isLoading = true;
        const payload = {
          sessionId: this.currentSessionId,
          dataSource: this.currentTabDataSource,
          featureId: feature[0].id,
        };
        try {
          await this.addFeatureToDelete(payload);
          this.isLoading = false;
        } catch {
          this.isLoading = false;
        }
      }
    },
    async getFeatureToAssign() {
      const displayAttributeName = this.zdmDataDataSourcesDisplayAttributesNames[this.currentTabName];
      const sessionObject = this.currentTabSessionObjects['insert']?.find(
        feature => feature.metadata.number === this.assignedFeaturesInput[this.currentTabName]
      );
      if (sessionObject) {
        this.assignFeature([
          {
            id: sessionObject.metadata.id,
            dataSource: this.currentTabDataSource,
            layerId: this.currentTabName,
            [displayAttributeName.split('.')[1]]: this.assignedFeaturesInput[this.currentTabName],
          },
        ]);
        return;
      }
      const layerId = this.currentTabLayer;
      const payload = {
        layer_id: layerId,
        features_filter: {
          $EQUAL: {
            [`${this.zdmDataDataSourcesDisplayAttributesNames[this.currentTabName]}`]: {
              value: this.assignedFeaturesInput[this.currentTabName],
            },
          },
        },
      };
      const r = await this.getLayerFeatures(payload);
      const feature = r.data.data.features.features[0];
      if (feature) {
        const { id, properties } = feature;
        const displayAttrbuteValue = properties[displayAttributeName];
        const formatedFeature = {
          id,
          dataSource: this.currentTabDataSource,
          layerId,
          [displayAttributeName.split('.')[1]]: displayAttrbuteValue,
        };
        this.assignFeature([formatedFeature]);
      } else {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.notFoundFeatureWithNumber'),
        });
      }
    },
    async assignFeature(f) {
      if (!f?.length) return;
      let feature = f[0];
      if (this.currentTabName === 'street_lamp') {
        this.isAssignedFeaturesLoading = true;
        try {
          let featureTree;
          if (this.currentTabName === feature.layerId) {
            const currentSessionObject = this.currentTabSessionObjectsInsert.find(
              obj => obj.metadata.id === feature.id
            );
            featureTree = {
              ...currentSessionObject?.metadata.street_lamp_objects,
              properties: currentSessionObject?.properties,
            };
          } else {
            featureTree = (await this.getZdmObjectTreeStructure({ datasource: feature.dataSource, id: feature.id }))
              .data.data;
          }
          feature = {
            ...feature,
            treeMetadata: featureTree,
            treeObjects: this.getChipsFromTree(featureTree.properties, featureTree),
          };
        } finally {
          this.isAssignedFeaturesLoading = false;
        }
      } else if (this.currentTabName === 'cable') {
        this.isAssignedFeaturesLoading = true;
        try {
          if (this.currentTabName === feature.layerId) {
            const relations = await this.getCableRelations({
              feature_id: feature.id,
              session: this.currentSessionId,
            });
            const currentSessionObject = this.currentTabSessionObjectsInsert.find(
              obj => obj.metadata.id === feature.id
            );
            feature = {
              ...feature,
              metadata: { ...currentSessionObject.metadata, ...relations },
              catalogCableType: this.getCatalogCableType(currentSessionObject),
            };
          } else {
            const rFeature = await this.getLayerFeature({ layer_id: feature.layerId, feature_id: feature.id });
            const relations = await this.getCableRelations({
              datasource: this.zdmDataDataSources['cable']?.datasource,
              feature_id: feature.id,
            });
            feature = {
              ...feature,
              metadata: { ...feature.metadata, ...relations },
              catalogCableType: this.getCatalogCableType(rFeature),
            };
          }
        } finally {
          this.isAssignedFeaturesLoading = false;
        }
      }
      this.$set(this.assignedFeatures, this.currentTabName, feature);
      this.$delete(this.assignedFeaturesInput, this.currentTabName);
    },
    unassignFeature() {
      this.$delete(this.assignedFeatures, this.currentTabName);
    },
    zoomToFeature(feature) {
      const { geometry } = feature;
      this.$root.$emit('createGeomAndFitView', {
        ...geometry,
        crs: { type: 'name', properties: { name: this.$_config.defaultEpsg } },
      });
    },
    zoomToAssignFeature() {
      const { layerId, id } = this.assignedFeatures[this.currentTabName];
      this.$root.$emit('zoomToFeature', layerId, layerId === this.currentTabName ? 'zdmEditorLayers' : 'layers', id);
    },
    isDisabled(value) {
      const layerId = this.zdmDataLayers[value];
      if (this.projectLayers.find(layer => layer.id === layerId)) {
        return false;
      }
      return true;
    },
    tooltipText(value) {
      return this.$i18n.t('sidebar.noSpecificLayerInProject', {
        value: this.$i18n.t(`admin.zdmStreetLights.catalogs.${value}`),
      });
    },
    toggleTabList(listType) {
      const currentState = this.tabsListExpanded[this.currentTabName]?.[listType] || false;
      if (this.tabsListExpanded[this.currentTabName]) {
        this.$set(this.tabsListExpanded, this.currentTabName, {
          ...this.tabsListExpanded[this.currentTabName],
          [listType]: !currentState,
        });
      } else {
        this.$set(this.tabsListExpanded, this.currentTabName, { [listType]: !currentState });
      }
    },
    copyPreviewObjectFromSession() {
      this.isCopyPreview = true;
      setTimeout(() => {
        this.isFeatureDialogLoaded = true;
        this.isFeatureDialogVisible = true;
      });
    },
    editObjectFromSession(feature) {
      this.currentEditFeature = JSON.parse(JSON.stringify(feature));
      this.isFeatureDialogLoaded = true;
      this.isFeatureDialogVisible = true;
    },
    startCurrentTabFeaturesModify() {
      this.setZdmEditLayerVisible(this.currentTabName, false);
      this.$root.$emit('modifySidebarGeometry', {
        geometryValue: this.currentTabSessionObjectsInsert.map(feature => {
          return {
            id: feature.metadata.id,
            geometry: { ...feature.geometry, crs: { properties: { name: this.$_config.defaultEpsg }, type: 'name' } },
            properties: feature.properties,
            type: 'Feature',
          };
        }),
        modifyendCallback: this.onCurrentTabFeaturesModifyend,
        withEditing: true,
        isInitSnapping: true,
        snappingAdditionalLayers: this.snappingAdditionalLayers,
        layerId: this.currentTabName === 'cable' ? this.currentTabLayer : null,
        activeLayer: this.currentTabName === 'cable' ? this.currentTabLayer : null,
        styleFunction: this.getCurrentTabFeaturesStyleFunction(),
        condition: e => {
          return this.isLoading ? never(e) : primaryAction(e);
        },
      });
    },
    async onCurrentTabFeaturesModifyend(e) {
      try {
        this.isLoading = true;
        for (const feature of e.features.getArray()) {
          const newFeature = prepareDrawnFeature(feature);
          const currentSessionFeature = this.currentTabSessionObjectsInsert.find(feature => {
            return feature.metadata.id === newFeature.id;
          });
          const data = {
            dataSource: this.currentTabDataSource,
            featureId: newFeature.id,
            sessionId: this.currentSessionId,
            payload: {
              properties: currentSessionFeature.properties,
              geometry: newFeature.geometry,
            },
          };
          await this.editFeatureFromSession(data);
          this.$nextTick(() => {
            this.setZdmEditLayerVisible(this.currentTabName, false);
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    setZdmEditLayerVisible(layerId, value) {
      this.$root.$emit('setZdmEditLayerVisible', { layerId, value });
    },
    getCurrentTabFeaturesStyleFunction() {
      const { style, geometryType, labelsVisible, id } = this.zdmEditorNewObjectsDataSources[this.currentTabDataSource];
      return f => this.getFeatureStyle(f, style, geometryType, labelsVisible, id);
    },
  },
  watch: {
    featureToAddGeometry(nV) {
      if (nV) {
        this.isFeatureDialogLoaded = true;
        this.isFeatureDialogVisible = true;
      }
    },
    currentTab(nV, oV) {
      if (oV || oV === 0) {
        this.setZdmEditLayerVisible(this.computedTabs[oV].value, true);
        this.$refs[`features-input-tab-${this.computedTabs[oV].value}`]?.[0]?.$refs['data-input']?.toggleOff();
        this.$refs[`feature-to-add-input-${this.computedTabs[oV].value}`]?.[0]?.$refs['data-input']?.toggleOff();
      }
      if (nV || nV === 0) {
        this.$nextTick(() => {
          this.startCurrentTabFeaturesModify();
        });
      }
    },
    isFeatureDialogVisible(nV) {
      if (!nV) {
        const hadGeometry = JSON.parse(JSON.stringify(this.currentEditFeature || '')) ? true : false;
        this.featureToAddGeometry = null;
        this.currentEditFeature = undefined;
        this.$nextTick(() => {
          if (!hadGeometry && !this.isCopyPreview) {
            this.$refs[`feature-to-add-input-${this.currentTabName}`]?.[0]?.$refs['data-input']?.toggleOn(
              'drawGeometry'
            );
          }
          this.isCopyPreview = false;
        });
      }
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const { isModificationInteractionActive, isDrawingInteractionActive, activeTool } = vm;
        return { isModificationInteractionActive, isDrawingInteractionActive, activeTool };
      },
      nV => {
        const { isModificationInteractionActive, isDrawingInteractionActive, activeTool } = nV;
        if (isDrawingInteractionActive || activeTool) {
          this.setZdmEditLayerVisible(this.currentTabName, true);
        } else if (
          !isModificationInteractionActive &&
          !isDrawingInteractionActive &&
          !activeTool &&
          !this.isFeatureDialogVisible
        ) {
          this.startCurrentTabFeaturesModify();
        }
      },
      {
        deep: true,
      }
    );
    for (const index in this.computedTabs) {
      if (!this.computedTabs[index].isTabDisabled) {
        this.currentTab = index;
        return;
      }
    }
  },
  created() {
    this.startCurrentTabFeaturesModify = debounce(this.startCurrentTabFeaturesModify, 500);
  },
};
</script>

<style lang="scss" scoped>
.list-element-insert {
  @include border(all, 1px, rgba(map-get($colors, 'success'), 0.5));
  background-color: rgba(map-get($colors, 'success'), 0.1);
}
.list-element-insert:hover {
  background-color: rgba(map-get($colors, 'success'), 0.2);
}
.list-element-delete {
  @include border(all, 1px, rgba(map-get($colors, 'error'), 0.5));
  background-color: rgba(map-get($colors, 'error'), 0.1);
}
.list-element-delete:hover {
  background-color: rgba(map-get($colors, 'error'), 0.2);
}
.extra-div {
  height: auto;
  margin: 0 20px 0 0;
}
.v-tab {
  letter-spacing: 0.0178571429em !important;
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  font-weight: 500;
  height: fit-content;
  text-transform: unset !important;
}
.activeTab {
  background-color: map-get($colors, 'primarybackground');
}
.custom-tabs {
  position: sticky;
  z-index: 1;
}
.v-tab:not(.activeTab) {
  color: #5f6368 !important;
}
.lantern-chips-group {
  ::v-deep {
    .v-slide-group__content {
      padding: 0px !important;
    }
    .v-slide-group__prev {
      margin-right: 2px !important;
    }
    .v-slide-group__next {
      margin-left: 2px !important;
    }
    .v-slide-group__next,
    .v-slide-group__prev {
      min-width: 17px !important;
      flex: 0 1 17px !important;
    }
    .v-chip {
      background-color: #e0e0e0 !important;
    }
    .v-chip--active::before {
      opacity: 0 !important;
    }
  }
}
::v-deep {
  .v-item-group {
    height: auto !important;
  }
  .v-window {
    overflow: initial !important;
  }
  .v-slide-group__prev {
    min-width: 40px !important;
    max-width: 8.33% !important;
    display: flex !important;
    flex-basis: 8.33% !important;
  }
  .v-badge {
    position: unset !important;
  }
  .v-badge__wrapper {
    position: relative !important;
    display: block;
  }
}
</style>
